// Extra Framework sections — extracted from the provided image:
// 1. Journey Flows (continuous 5-step horizontal stepper)
// 2. "Wealth Accumulation Towards Early Retirement" big editorial watermark
// 3. Key Insights — Leverage + 80/20
// 4. What You Get With A Homely Abode — 4-card grid

import { Quote, Compass, Layers, BarChart3, Telescope } from "lucide-react";

const JOURNEY_STEPS = [
  {
    n: "01",
    title: "Get clear on your why",
    body: "Strategy first, unit second. Know your goal before you pick a property.",
  },
  {
    n: "02",
    title: "Enter the right asset",
    body: "Your first move shapes the next three. Choose with the exit in mind.",
  },
  {
    n: "03",
    title: "Let equity do the work",
    body: "Upgrade using accumulated equity — not savings alone. Leverage wisely.",
  },
  {
    n: "04",
    title: "Build income from your asset",
    body: "Structure your property to generate passive income funding your lifestyle.",
  },
  {
    n: "05",
    title: "Exit on your own terms",
    body: "Rightsize from strength. Retire with options, not obligations.",
  },
];

const PROMISES = [
  { Icon: Compass, head: "Clarity,", sub: "not pressure", body: "Every decision feels informed, not rushed." },
  { Icon: Layers, head: "Strategy,", sub: "not advice", body: "You leave with a concrete next step, not a business card." },
  { Icon: BarChart3, head: "Data,", sub: "not gut feel", body: "Numbers back every recommendation we make." },
  { Icon: Telescope, head: "Long view,", sub: "not this sale", body: "We think about where this move takes you in 10 years." },
];

/* --- 1. Journey Flows -------------------------------------------------- */
export function JourneyFlowsSection() {
  return (
    <section data-testid="framework-journey" className="section-pad bg-beige/60">
      <div className="container-page">
        <div className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-bronze" /> How the journey flows
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
          A <span className="italic text-bronze">continuous</span> strategy — not a one-off sale.
        </h2>

        <div className="relative mt-16 md:mt-20">
          {/* Connecting line behind dots */}
          <span
            aria-hidden
            className="hidden md:block absolute top-6 left-[6%] right-[6%] h-px bg-bronze/30"
          />

          <ol className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
            {JOURNEY_STEPS.map((s, i) => (
              <li
                key={s.n}
                data-testid={`journey-step-${s.n}`}
                className="reveal relative flex flex-col items-start md:items-center md:text-center"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span
                  className={`relative h-12 w-12 rounded-full flex items-center justify-center font-serif text-lg z-10 ${
                    i === 0 || i === JOURNEY_STEPS.length - 1
                      ? "bg-bronze text-cream"
                      : "bg-cream border border-bronze/40 text-bronze"
                  }`}
                >
                  •
                </span>
                <div className="mt-5 text-[0.7rem] tracking-[0.28em] uppercase text-olive">
                  Step {s.n}
                </div>
                <h3 className="mt-2 font-serif italic text-2xl text-bronze leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm font-light text-ink_soft/80 leading-relaxed max-w-[18rem]">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* --- 2. Wealth Accumulation watermark headline ------------------------- */
export function WealthHeadlineSection() {
  const words = [
    { t: "WEALTH", emphasize: false },
    { t: "ACCUMULATION", emphasize: true, letter: "A" },
    { t: "TOWARDS", emphasize: false },
    { t: "EARLY", emphasize: false },
    { t: "RETIREMENT", emphasize: false },
  ];
  return (
    <section
      data-testid="framework-wealth-headline"
      className="relative overflow-hidden bg-cream section-pad"
    >
      {/* large faded "A" watermark */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-10 top-1/2 -translate-y-1/2 font-serif text-[28rem] leading-none text-bronze/[0.06] select-none hidden lg:block"
      >
        A
      </div>

      <div className="container-page relative">
        <div className="eyebrow mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-bronze" /> The North Star
        </div>

        <div className="space-y-3 md:space-y-5">
          {words.map((w, i) => (
            <h2
              key={w.t}
              className="reveal font-serif font-semibold text-bronze tracking-[0.05em] leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[6rem]"
              style={{ transitionDelay: `${i * 80}ms` }}
              data-testid={`wealth-word-${i}`}
            >
              {w.emphasize && w.letter ? (
                <>
                  <span className="text-bronze">{w.letter}</span>
                  <span className="text-bronze/80">{w.t.slice(1)}</span>
                </>
              ) : (
                <span className="text-bronze/80">{w.t}</span>
              )}
            </h2>
          ))}
        </div>

        <p className="mt-12 max-w-2xl text-base md:text-lg font-light text-ink_soft/80 leading-relaxed">
          The W.A.T.E.R framework exists for one outcome: a Singapore homeowner who reaches
          financial freedom through property — earlier than they thought possible, and with options
          when they get there.
        </p>
      </div>
    </section>
  );
}

/* --- 3. Key Insights --------------------------------------------------- */
export function KeyInsightsSection() {
  return (
    <section data-testid="framework-key-insights" className="section-pad bg-beige/60">
      <div className="container-page">
        <div className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-bronze" /> Key Insights
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
          Two ideas that <span className="italic text-bronze">change everything.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-7">
          <article
            data-testid="insight-leverage"
            className="reveal relative border border-bronze/25 bg-cream p-8 md:p-10"
          >
            <Quote className="text-bronze" size={28} />
            <div className="mt-3 text-[0.7rem] tracking-[0.28em] uppercase text-olive">
              The Leverage Insight
            </div>
            <p className="mt-4 font-serif italic text-2xl md:text-3xl text-ink leading-snug">
              Most people save their way to a home. Few leverage their way to wealth.
            </p>
            <div className="my-7 shimmer-line" />
            <p className="text-base font-light text-ink_soft/80 leading-relaxed">
              There's a meaningful difference between paying down a mortgage and using property as a
              compounding asset. The W.A.T.E.R framework is built on the second — using the bank's
              capital alongside yours so equity grows faster than income alone allows.
            </p>
          </article>

          <article
            data-testid="insight-8020"
            className="reveal relative border border-bronze/25 bg-cream p-8 md:p-10"
          >
            <Quote className="text-bronze" size={28} />
            <div className="mt-3 text-[0.7rem] tracking-[0.28em] uppercase text-olive">
              The 80 / 20 Insight
            </div>
            <p className="mt-4 font-serif italic text-2xl md:text-3xl text-ink leading-snug">
              80% of your outcome comes from 20% of decisions made early.
            </p>
            <div className="my-7 shimmer-line" />
            <p className="text-base font-light text-ink_soft/80 leading-relaxed">
              Most homeowners spend enormous energy picking the right unit — and almost none on the
              strategy around it. Entry point, hold period, upgrade timing, exit plan: these four
              decisions drive the bulk of your outcome.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

/* --- 4. What You Get With A Homely Abode ------------------------------- */
export function PromisesSection() {
  return (
    <section data-testid="framework-promises" className="section-pad bg-cream">
      <div className="container-page">
        <div className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-bronze" /> What you get with A Homely Abode
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
          Four <span className="italic text-bronze">promises</span> — kept every meeting.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROMISES.map(({ Icon, head, sub, body }, i) => (
            <div
              key={head}
              data-testid={`promise-card-${i}`}
              className="reveal border-t-2 border-bronze bg-beige/40 p-7 md:p-8 hover:bg-beige transition-colors flex flex-col"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <Icon className="text-bronze" size={26} />
              <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ink leading-tight">
                {head}
                <br />
                <span className="italic text-bronze">{sub}</span>
              </h3>
              <div className="my-6 shimmer-line" />
              <p className="text-sm font-light text-ink_soft/80 leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
